import React from 'react';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Checkbox, CircularProgress, Stack, TextField } from '@mui/material';

import { errMsg } from './utils';

const CHECKBOX_ICON = <CheckBoxOutlineBlank fontSize="small" />;
const CHECKBOX_CHECKED_ICON = <CheckBox fontSize="small" />;

interface Props {
  chainOptions: Array<string>;
  selectedChains: Array<string>;
  onChainsChange: (chainNames: Array<string>) => void;
  isChainsLoading: boolean;
  isChainsError: boolean;
  chainsError: unknown;
  regionOptions: Array<string>;
  region: string | null;
  onRegionChange: (region: string | null) => void;
}

export default function ChainAndRegionPicker({
  chainOptions,
  selectedChains,
  onChainsChange,
  isChainsLoading,
  isChainsError,
  chainsError,
  regionOptions,
  region,
  onRegionChange,
}: Props) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Autocomplete<string, true>
          multiple
          disableCloseOnSelect
          options={chainOptions}
          value={selectedChains}
          onChange={(_, value) => onChainsChange(value)}
          loading={isChainsLoading}
          sx={{ minWidth: 320, flex: 1, maxWidth: 720 }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  icon={CHECKBOX_ICON}
                  checkedIcon={CHECKBOX_CHECKED_ICON}
                  checked={selected}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {option}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Hotel chain(s)"
              size="small"
              placeholder={selectedChains.length === 0 ? 'Search chains…' : undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isChainsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete<string>
          options={regionOptions}
          value={region}
          onChange={(_, value) => onRegionChange(value)}
          sx={{ minWidth: 200 }}
          renderInput={(params) => <TextField {...params} label="Region" size="small" placeholder="All regions" />}
        />
      </Stack>

      {isChainsError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load chains: {errMsg(chainsError)}
        </Alert>
      )}
    </Box>
  );
}
